<script lang="ts">
	import LandingPageLayout from '$components/LandingPageLayout.svelte';
	import LoginOrSignUpModal from '$components/LoginOrSignUpModal.svelte';
	import type { User } from 'firebase/auth';
	import GridGallery from '$components/GridGallery.svelte';
	import CTA from '$components/CTA.svelte';
	import Transition from 'svelte-transition';
	import { onMount } from 'svelte';

	let user: User | null | undefined = undefined;
	let loginOrSignUpModal: LoginOrSignUpModal;
	let loginOrSignupWhichModal: 'login' | 'sign-up' = 'sign-up';

	let showVideo = false;

	onMount(() => {
		showVideo = true;
	});
</script>

<svelte:head>
	<title>Iliad: DnD Character Generator</title>
	<meta
		name="description"
		content="Craft unique characters with an expressive fantasy character portrait generator designed for creative control. Create illustrations, concept art, portraits, and icons with precise control over your vision."
	/>
	<meta
		property="og:image"
		content="https://imagedelivery.net/vSd3Qxd5RvTJoQinGHZ4vg/bcf31952-4c49-4084-4d2a-5f2d9ea6ca00/public"
	/>
	<link
		rel="preload"
		href="/fonts/crimson-text-v19-latin-regular.woff2"
		as="font"
		type="font/woff2"
		crossorigin="anonymous"
	/>
</svelte:head>

<LandingPageLayout bind:user bind:loginOrSignUpModal bind:loginOrSignupWhichModal>
	<div class="landing-page-width">
		<div class="h-16"></div>
		<div class="flex flex-row justify-start items-center">
			<div>
				<a href="/" class="text-cyan-600">Home</a>
			</div>
			<div class="w-2"></div>
			<div>/</div>
			<div class="w-2"></div>
			<div>AI DnD character creator</div>
		</div>
		<div class="h-16"></div>
		<h1 class="text-3.5xl sm:text-5xl text-left font-crimson text-zinc-900 z-10">
			AI DnD character creator
		</h1>
		<div class="h-12"></div>
		<p>
			Craft unique characters with an expressive fantasy character portrait generator designed for
			creative control.
		</p>
		<div class="h-40 flex flex-col justify-center items-start">
			<CTA
				{user}
				href="/canvas"
				eventText="create_characters"
				{loginOrSignUpModal}
				{loginOrSignupWhichModal}
			>
				<i class="fa-solid fa-paintbrush mr-2"></i> Create characters
			</CTA>
		</div>
		<div class="h-12"></div>
		<h2 class=" text-xl md:text-2xl font-semibold">Generate fantasy character portraits.</h2>
		<div class="h-6"></div>
		Discover and mix artistic presets to fine-tune your world's appearance.
		<div class="h-6"></div>
		<div class="h-28 sm:h-48">
			<GridGallery
				images={[
					'https://imagedelivery.net/vSd3Qxd5RvTJoQinGHZ4vg/bcf31952-4c49-4084-4d2a-5f2d9ea6ca00/smaller',
					'https://imagedelivery.net/vSd3Qxd5RvTJoQinGHZ4vg/9b730df3-3224-4518-b975-2c7293076a00/smaller',
					'https://imagedelivery.net/vSd3Qxd5RvTJoQinGHZ4vg/4573c850-3ee1-4ba9-88fc-b1534a0b6c00/smaller'
				]}
				alts={['dnd paladin art', 'dnd dragon art', 'dnd bard art']}
			/>
		</div>
		<div class="h-24"></div>

		<h2 class="text-xl md:text-2xl font-semibold">See how Iliad's canvas works.</h2>
		<div class="h-8"></div>
		<div class="w-full h-[21rem] sm:h-[32rem] md:h-[39rem] rounded overflow-hidden bg-zinc-900">
			<Transition
				show={showVideo}
				enter="ease-in-out duration-1000"
				enterFrom="opacity-0"
				enterTo="opacity-100"
				leave="ease-in-out duration-1000"
				leaveFrom="opacity-100"
				leaveTo="opacity-0"
			>
				<mux-player
					playback-id="500say01j02e2J3vZIkfzU7oIxzFNi0212OrG7BrdfH2chg"
					metadata-video-title="expressive fantasy 2d character creation"
					metadata-viewer-user-id=""
					primary-color="#ffffff"
					secondary-color="#000000"
					accent-color="#22D3EE"
					autoplay="true"
				></mux-player>
			</Transition>
		</div>

		<div class="h-24"></div>

		<h2 class=" text-xl md:text-2xl font-semibold">Create practical DnD concept art.</h2>
		<div class="h-6"></div>
		Conjure cover art, items, battle maps, and environments into existence.
		<div class="h-6"></div>
		<div class="h-56 sm:h-96">
			<GridGallery
				images={[
					'https://imagedelivery.net/vSd3Qxd5RvTJoQinGHZ4vg/a7a25a32-05e0-4fac-b8bf-a1d162d7a400/public',
					'https://imagedelivery.net/vSd3Qxd5RvTJoQinGHZ4vg/be5d5b4d-8a38-4ec7-79e8-8200b6fc0500/public'
				]}
				alts={['dnd cover art', 'ai generated battle maps']}
				cols={2}
			/>
		</div>

		<div class=" h-40 flex flex-col justify-center items-start">
			<CTA
				{user}
				href="/canvas"
				eventText="start_creating"
				{loginOrSignUpModal}
				{loginOrSignupWhichModal}
			>
				<i class="fa-solid fa-paintbrush mr-2"></i> Start creating
			</CTA>
		</div>
		<div class="h-32"></div>
	</div>
</LandingPageLayout>
